import { useTranslation } from '@/contexts/i18n-context';
import { Button } from '../button';
import { Modal, ModalButtonContainer } from '../modal/modal';
import { Checkbox } from '../inputs/checkbox';
import styles from './treatment-plan-read-modal.module.css';
import { Form } from '../form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import { TreatmentPlanSvg } from '../svgs/treatment-plan';
import { GetTreatmentPlanResponse } from '@/api/api.schemas';
import { useMarkTreatmentPlanAsRead } from '@/api/mark-treatment-plan-as-read';
import { useQueryClient } from '@tanstack/react-query';
import { getGetTreatmentPlanQueryKey } from '@/api/get-treatment-plan';

const translationBase = 'visit.treatmentPlan.readModal';

const schema = z.object({
  treatmentPlanRead: z.literal(true),
});

type FormValues = z.infer<typeof schema>;

interface TreatmentPlanReadModalProps {
  treatmentPlan: GetTreatmentPlanResponse;
}

export function TreatmentPlanReadModal({ treatmentPlan }: TreatmentPlanReadModalProps) {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const { mutate: markAsRead, isPending } = useMarkTreatmentPlanAsRead();

  const queryClient = useQueryClient();

  const onConfirm = () => {
    markAsRead(
      { encounterId: treatmentPlan.encounterId },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: getGetTreatmentPlanQueryKey(treatmentPlan.encounterId),
          }),
      },
    );
  };

  const treatmentPlanRead = form.watch('treatmentPlanRead');

  return (
    <Modal isOpen={treatmentPlan.treatmentPlanUnread} hideCloseButton>
      <Form form={form} onSubmit={onConfirm} className={styles.form}>
        <TreatmentPlanSvg className={styles.treatmentPlanSvg} />
        <h2 className="h3">{t(`${translationBase}.title`)}</h2>
        <p className={styles.description}>{t(`${translationBase}.description`)}</p>
        <Checkbox name="treatmentPlanRead" label={t(`${translationBase}.checkboxLabel`)} />
        <ModalButtonContainer>
          <Button disabled={!treatmentPlanRead} loading={isPending} type="submit">
            {t(`${translationBase}.confirmButton`)}
          </Button>
        </ModalButtonContainer>
      </Form>
    </Modal>
  );
}
